import { useEffect, useState } from 'react';
import { User, Lock } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { getWidget } from '../lib/api';

const ClientLoginPage = () => {
  const [widget, setWidget] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWidget = async () => {
      try {
        const res = await getWidget('client-login');
        setWidget(res.data);
      } catch (error) {
        console.error('Error fetching client login widget:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchWidget();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="client-login-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <p className="text-xs sm:text-sm font-semibold tracking-widest uppercase text-neutral-500 mb-3 sm:mb-4">For Our Clients</p>
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold tracking-tight leading-tight text-neutral-900 mb-4 sm:mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>Client Login</h1>
          <p className="text-base sm:text-lg md:text-xl leading-relaxed text-neutral-500 max-w-2xl mx-auto px-4">
            Access your client portal to manage your event details, playlists, and more.
          </p>
        </div>
      </section>

      {/* Widget Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-neutral-50">
        <div className="max-w-xl mx-auto px-4 sm:px-6 md:px-12">
          {widget?.is_active && widget?.code ? (
            <div 
              className="widget-container bg-white p-6 sm:p-8 rounded-lg shadow-sm"
              dangerouslySetInnerHTML={{ __html: widget.code }}
            />
          ) : (
            <div className="text-center py-12 sm:py-16 bg-white rounded-lg">
              <div className="w-20 h-20 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="w-10 h-10 text-neutral-400" />
              </div>
              <p className="text-neutral-500 text-base sm:text-lg">Client portal coming soon!</p>
              <p className="text-neutral-400 text-sm mt-2">We're setting up a dedicated client area for you.</p>
              <a href="/contact" className="inline-block mt-6 bg-neutral-900 text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-neutral-800 transition-colors">
                Contact Us For Access
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8 sm:mb-12 text-center" style={{ fontFamily: 'Syne, sans-serif' }}>Client Portal Features</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-neutral-50 p-6 rounded-lg text-center">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <svg className="w-6 h-6 text-neutral-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
              </div>
              <h3 className="font-semibold mb-2">Playlist Management</h3>
              <p className="text-neutral-500 text-sm">Create and manage your must-play and do-not-play lists.</p>
            </div>
            <div className="bg-neutral-50 p-6 rounded-lg text-center">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <svg className="w-6 h-6 text-neutral-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-semibold mb-2">Event Timeline</h3>
              <p className="text-neutral-500 text-sm">View and update your event schedule and special moments.</p>
            </div>
            <div className="bg-neutral-50 p-6 rounded-lg text-center sm:col-span-2 lg:col-span-1">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <svg className="w-6 h-6 text-neutral-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="font-semibold mb-2">Direct Messaging</h3>
              <p className="text-neutral-500 text-sm">Communicate directly with your DJ about your event.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ClientLoginPage;
